import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import ImageUpload from "@/components/app/ImageUpload";

export const emptyVenue = { name: "", short_code: "", address: "", contact_name: "", contact_phone: "", notes: "", training_seasons: [], is_unavailable: false, unavailable_from: "", unavailable_until: "", unavailable_reason: "", closed_days: [], needs_external_confirmation: false, is_game_day: false, league_earliest_hour: "" };

export function VenueForm({ value, onChange, venueId }) {
  const upd = (k) => (e) => onChange({ ...value, [k]: e.target.value });
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-3 gap-3">
        <div className="col-span-2"><Label className="text-xs uppercase tracking-widest opacity-70">Name</Label><Input value={value.name} onChange={upd("name")} className="mt-2" placeholder="Elsenham Recreation Ground" /></div>
        <div>
          <Label className="text-xs uppercase tracking-widest opacity-70">Short code</Label>
          <Input
            value={value.short_code || ""}
            onChange={e => onChange({ ...value, short_code: e.target.value.toUpperCase().slice(0, 4) })}
            data-testid="venue-short-code"
            maxLength={4}
            className="mt-2 uppercase tracking-wider"
            placeholder="REC"
          />
          <p className="text-[10px] opacity-50 mt-1.5">2–4 chars. Blank = auto-acronym from name.</p>
        </div>
      </div>
      <div><Label className="text-xs uppercase tracking-widest opacity-70">Address</Label><Input value={value.address || ""} onChange={upd("address")} className="mt-2" /></div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label className="text-xs uppercase tracking-widest opacity-70">Contact name</Label><Input value={value.contact_name || ""} onChange={upd("contact_name")} className="mt-2" /></div>
        <div><Label className="text-xs uppercase tracking-widest opacity-70">Contact phone</Label><Input value={value.contact_phone || ""} onChange={upd("contact_phone")} className="mt-2" /></div>
      </div>
      <div><Label className="text-xs uppercase tracking-widest opacity-70">Notes</Label><Textarea rows={2} value={value.notes || ""} onChange={upd("notes")} className="mt-2" /></div>
      {/* External-confirmation flag — for pitches booked via a school hall etc.
          where our calendar reserves the slot but the venue still needs a
          separate email/phone confirmation before the coach is "safe" to run
          the session. Bookings created at this venue land in "Awaiting
          confirmation" and the coach flips them to "Confirmed" from the
          detail dialog once the venue replies. */}
      <div className={`rounded border p-3 ${value.is_game_day ? "border-[#83B68B]/60 bg-[#83B68B]/10" : "border-border"}`}>
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={!!value.is_game_day}
            onChange={e => onChange({ ...value, is_game_day: e.target.checked })}
            data-testid="venue-is-game-day"
            className="mt-1 accent-[#83B68B]"
          />
          <div>
            <div className="text-xs tracking-widest uppercase font-bold text-[#83B68B]">Game Day venue</div>
            <div className="text-xs opacity-60 mt-1">This venue hosts matches. The calendar opens in <strong>Game Day</strong> mode by default, showing only Game Day venues — users can switch to <strong>All</strong> to see every venue.</div>
          </div>
        </label>
      </div>
      <div className={`rounded border p-3 ${value.league_earliest_hour !== "" && value.league_earliest_hour != null ? "border-[#83B68B]/60 bg-[#83B68B]/10" : "border-border"}`}>
        <div className="text-xs tracking-widest uppercase font-bold text-[#83B68B]">League game curfew</div>
        <div className="text-xs opacity-60 mt-1 mb-2">Hard block: league games at this venue cannot start before this time. Only admins can book earlier. Leave blank for no limit.</div>
        <select
          value={value.league_earliest_hour ?? ""}
          onChange={e => onChange({ ...value, league_earliest_hour: e.target.value === "" ? "" : Number(e.target.value) })}
          data-testid="venue-league-earliest-hour"
          className="bg-background border border-border rounded px-2 py-1 text-sm"
        >
          <option value="">No limit</option>
          {Array.from({ length: 16 }, (_, i) => i + 6).map(h => <option key={h} value={h}>{String(h).padStart(2, "0")}:00</option>)}
        </select>
      </div>
      <div className={`rounded border p-3 ${value.needs_external_confirmation ? "border-amber-500/60 bg-amber-500/10" : "border-border"}`}>
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={!!value.needs_external_confirmation}
            onChange={e => onChange({ ...value, needs_external_confirmation: e.target.checked })}
            data-testid="venue-needs-external-confirmation"
            className="mt-1 accent-amber-500"
          />
          <div>
            <div className="text-xs tracking-widest uppercase font-bold text-amber-300">Needs to confirm prior to booking</div>
            <div className="text-xs opacity-60 mt-1">Bookings at this venue are marked <strong>Awaiting confirmation</strong> until the coach confirms externally with the venue — they then tap <strong>Booking confirmed</strong> in the booking detail dialog. Use this for school halls, community pitches, and any ground where our calendar reservation isn't the source of truth.</div>
          </div>
        </label>
      </div>
      <div className={`rounded border p-3 ${value.is_unavailable ? "border-red-500/60 bg-red-500/10" : "border-border"}`}>
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={!!value.is_unavailable}
            onChange={e => onChange({ ...value, is_unavailable: e.target.checked })}
            data-testid="venue-is-unavailable"
            className="mt-1 accent-red-500"
          />
          <div>
            <div className="text-xs tracking-widest uppercase font-bold text-red-300">Mark venue as unavailable</div>
            <div className="text-xs opacity-60 mt-1">Blocks new bookings on any pitch at this venue and stops the auto-scheduler from placing FA fixtures here. Saving will also cancel every confirmed booking that falls inside the window below and email each booker.</div>
          </div>
        </label>
        {value.is_unavailable && (
          <div className="mt-3 grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] tracking-widest uppercase opacity-70">From (inclusive)</Label>
              <Input
                type="date"
                data-testid="venue-unavailable-from"
                className="mt-1.5"
                value={value.unavailable_from || ""}
                onChange={e => onChange({ ...value, unavailable_from: e.target.value })}
              />
              <p className="text-[10px] opacity-50 mt-1">Blank = start today.</p>
            </div>
            <div>
              <Label className="text-[10px] tracking-widest uppercase opacity-70">Until (inclusive)</Label>
              <Input
                type="date"
                data-testid="venue-unavailable-until"
                className="mt-1.5"
                value={value.unavailable_until || ""}
                onChange={e => onChange({ ...value, unavailable_until: e.target.value })}
              />
              <p className="text-[10px] opacity-50 mt-1">Blank = indefinite. Bookings after this date stay live.</p>
            </div>
            <div className="col-span-2">
              <Label className="text-[10px] tracking-widest uppercase opacity-70">Reason (optional — shown in cancellation email)</Label>
              <Textarea
                rows={2}
                data-testid="venue-unavailable-reason"
                className="mt-1.5"
                value={value.unavailable_reason || ""}
                onChange={e => onChange({ ...value, unavailable_reason: e.target.value })}
                placeholder="e.g. Pitch waterlogged, groundskeeper unavailable, closure notice from parish council…"
              />
            </div>
          </div>
        )}
      </div>
      <div>
        <Label className="text-xs uppercase tracking-widest opacity-70">Weekly closed days</Label>
        <div className="mt-2 flex flex-wrap gap-1.5" data-testid="venue-closed-days">
          {[
            { v: 0, label: "Mon" },
            { v: 1, label: "Tue" },
            { v: 2, label: "Wed" },
            { v: 3, label: "Thu" },
            { v: 4, label: "Fri" },
            { v: 5, label: "Sat" },
            { v: 6, label: "Sun" },
          ].map(({ v, label }) => {
            const selected = (value.closed_days || []).includes(v);
            return (
              <button
                type="button"
                key={v}
                onClick={() => {
                  const cur = value.closed_days || [];
                  const next = selected ? cur.filter(x => x !== v) : [...cur, v].sort((a, b) => a - b);
                  onChange({ ...value, closed_days: next });
                }}
                data-testid={`venue-closed-day-${v}`}
                className={`w-14 py-1.5 rounded text-xs tracking-widest uppercase border transition-colors ${
                  selected
                    ? "bg-red-500/20 border-red-400 text-red-200"
                    : "border-border hover:border-red-400 hover:text-red-300"
                }`}
              >{label}</button>
            );
          })}
        </div>
        <p className="text-[10px] opacity-50 mt-1.5">Ticked days become non-bookable on the calendar (e.g. "closed Mondays"). Bookings can't be created, and the auto-scheduler skips these weekdays.</p>
      </div>
      <div>
        <Label className="text-xs uppercase tracking-widest opacity-70">Training seasons</Label>
        <div className="mt-2 flex gap-2">
          {[["winter","Winter Training"],["summer","Summer Training"]].map(([k, label]) => {
            const selected = (value.training_seasons || []).includes(k);
            return (
              <button
                type="button"
                key={k}
                onClick={()=>{
                  const cur = value.training_seasons || [];
                  const next = selected ? cur.filter(x => x !== k) : [...cur, k];
                  onChange({ ...value, training_seasons: next });
                }}
                data-testid={`venue-training-${k}`}
                className={`px-3 py-1.5 rounded text-xs tracking-widest uppercase border ${
                  selected
                    ? "bg-[#83B68B] text-white border-[#83B68B]"
                    : "border-border hover:border-[#83B68B] hover:text-[#83B68B]"
                }`}
              >{label}</button>
            );
          })}
        </div>
        <p className="text-[10px] opacity-50 mt-1.5">Tag which training seasons this venue is used for — appears as a badge on the venue card and in filters later.</p>
      </div>
      {venueId && (
        <div className="pt-2 border-t border-border">
          <Label className="text-xs uppercase tracking-widest opacity-70">Venue photo</Label>
          <div className="mt-2">
            <ImageUpload
              endpoint={`/files/venue/${venueId}`}
              currentPath={value.photo_path}
              onUploaded={(r) => onChange({ ...value, photo_path: r.storage_path })}
              testId="venue-photo-upload"
              label="Upload photo"
            />
          </div>
        </div>
      )}
    </div>
  );
}
